import { css } from '@emotion/react'
import { Button, Cell, Grid, Heading } from 'bold-ui'
import { EquipeSelectField, Form, NumberField, SEM_EQUIPE_ITEM, UnidadeSaudeSelectField } from 'components/form'
import { LotacaoAndEstagioSelectField } from 'components/form/field/select/LotacaoAndEstagioSelectField/LotacaoAndEstagioSelectField'
import { confirm } from 'components/modals/confirm'
import { FormApi } from 'final-form'
import { useReterritorializarMutation } from 'graphql/hooks.generated'
import { UnidadeSaude } from 'graphql/types.generated'
import { FormRenderProps } from 'react-final-form'
import { isUndefinedOrNull } from 'util/checks'
import { metaPath } from 'util/metaPath'

import { createAtualizaImoveisFormCalculator } from './calculator'
import { AtualizacaoImoveisModal } from './components/AtualizacaoImoveisModal'
import { ReterritorializacaoAtualizacaoFormModel } from './model'
import { validateAtualizacaoImoveisForm } from './validator'

interface ReterritorializacaoAtualizacaoFormProps {
  changeToProcessingState(): void
  unidadeSaude: UnidadeSaude
  checkedRowItems: { imoveisId: ID[]; numeroCidadaos: number }
  disabled?: boolean
  progressToken: string
}
const meta = metaPath<ReterritorializacaoAtualizacaoFormModel>()

export const ReterritorializacaoAtualizacaoForm = ({
  changeToProcessingState,
  unidadeSaude,
  checkedRowItems: { numeroCidadaos, imoveisId },
  disabled,
  progressToken,
}: ReterritorializacaoAtualizacaoFormProps) => {
  const [reterritorializar] = useReterritorializarMutation()

  const onHandleSubmit = (formValues: ReterritorializacaoAtualizacaoFormModel, formApi: FormApi) => {
    confirm({
      title: <Heading level={2}> Confirmar atualização dos imóveis?</Heading>,
      body: <AtualizacaoImoveisModal numeroImoveis={imoveisId.length} numeroCidadaos={numeroCidadaos} />,
      onConfirm: async () => {
        formApi.getRegisteredFields().forEach((field) => formApi.resetFieldState(field))
        setTimeout(formApi.reset)
        changeToProcessingState()
        await reterritorializar({
          variables: {
            input: {
              novaMicroarea: formValues.novaMicroarea,
              lotacaoId: formValues.lotacao.id,
              imoveisId: imoveisId,
              progressToken: progressToken,
            },
          },
        })
      },
    })()
  }
  const semImoveisEncontrados = imoveisId.length === 0

  const renderForm = ({ handleSubmit, values }: FormRenderProps<ReterritorializacaoAtualizacaoFormModel>) => {
    return (
      <Grid
        style={css`
          width: 100%;
          margin-bottom: 1rem;
        `}
      >
        <Cell size={3}>
          <UnidadeSaudeSelectField
            name={meta.unidadeSaude}
            label='Unidade de Saúde'
            defaultValue={unidadeSaude}
            disabled={disabled}
            required
            dataTestId='UnidadeSaudeSelectField.AtualizarImovel'
          />
        </Cell>
        <Cell size={2}>
          <EquipeSelectField
            name={meta.equipe}
            unidadeSaudeId={values.unidadeSaude?.id}
            disabled={disabled || isUndefinedOrNull(values?.unidadeSaude)}
            label='Equipe'
            showSemEquipeOption
            required
            dataTestId='EquipeSelectField.AtualizarImovel'
          />
        </Cell>
        <Cell size={3}>
          <LotacaoAndEstagioSelectField
            name={meta.lotacao}
            label='Profissional'
            unidadeSaudeId={values.unidadeSaude?.id}
            equipeId={values.equipe?.id}
            disabled={disabled || isUndefinedOrNull(values?.equipe)}
            semEquipe={values.equipe?.id === SEM_EQUIPE_ITEM.id}
            required
            canWriteAtendimento={false}
            canWriteRegistroTardio={false}
            canWriteCadastroDomiciliar
            canWriteCadastroIndividual
            excluirEsbEmulti
          />
        </Cell>
        <Cell size={2}>
          <NumberField
            name={meta.novaMicroarea}
            label='Microárea'
            maxLength={2}
            minLength={2}
            disabled={disabled}
            required
          />
        </Cell>
        <Cell size={2}>
          <Button
            kind='primary'
            size='small'
            style={css`
              margin-top: 1.5rem;
            `}
            onClick={handleSubmit}
            disabled={disabled || isSubmitButtonDisable(values, semImoveisEncontrados)}
          >
            Atualizar imóveis
          </Button>
        </Cell>
      </Grid>
    )
  }

  return (
    <Form<ReterritorializacaoAtualizacaoFormModel>
      render={renderForm}
      validate={validateAtualizacaoImoveisForm}
      onSubmit={onHandleSubmit}
      decorators={decorators}
    />
  )
}

const decorators = [createAtualizaImoveisFormCalculator(meta)]

const isSubmitButtonDisable = (values: ReterritorializacaoAtualizacaoFormModel, semImoveisEncontrados: boolean) =>
  !(values.novaMicroarea && values.unidadeSaude && values.lotacao && values.equipe) || semImoveisEncontrados
